import {AfriexAdminPermission} from '@afriex/core/domain/enums';

import {
  Button,
  PermissionsProvider,
  Table,
  TBody,
  Td,
  Th,
  Thead,
  Tr,
} from '@/components';
import type {NotifyModalProps} from '@/screens/Processor/Metrics/NotifyModal';
import type {Metrics, MetricsData} from '@/screens/Processor/Metrics/types';
import {formatTime} from '@/screens/Processor/Metrics/utils';

export interface SPCTableProps {
  metrics: Metrics;
  data: MetricsData[];
  setShowNotifyModal: (value: React.SetStateAction<boolean>) => void;
  setNotifyModalProps: (
    value: React.SetStateAction<
      Pick<
        NotifyModalProps,
        | 'message'
        | 'selectedProcessor'
        | 'selectedCorridorPair'
        | 'selectedPaymentInstrument'
      >
    >,
  ) => void;
}
export const ratesHeaderHue = 'bg-blue-50 p-8 shadow-sm sticky';
const SuccessPerCorridorTable = ({
  metrics,
  data,
  setNotifyModalProps,
  setShowNotifyModal,
}: SPCTableProps) => {
  return (
    <Table className="w-full">
      <Thead className={ratesHeaderHue}>
        <Tr enableHover={false}>
          <Th className="px-3">Corridor</Th>
          <Th className="px-2">Time to Success (Avg)</Th>
          <Th className="px-2">Prev Time to Success (Avg)</Th>
          <Th className="px-2">&#916; in Time to Success (Avg)</Th>
          <Th className="px-2">Processor</Th>
          <Th className="px-2">Success Rate (%)</Th>
          <Th className="px-2"></Th>
        </Tr>
      </Thead>

      <TBody>
        {Object.keys(metrics.corridorSuccessRates)
          .sort(
            (a, b) =>
              metrics.corridorSuccessRates[a] - metrics.corridorSuccessRates[b],
          )
          .map((corridorPair, index) => (
            <SPCTRow
              key={corridorPair}
              metrics={metrics}
              corridor={corridorPair}
              data={data}
              setNotifyModalProps={setNotifyModalProps}
              setShowNotifyModal={setShowNotifyModal}
              index={index}
            />
          ))}
      </TBody>
    </Table>
  );
};

const SPCTRow = ({
  metrics,
  corridor,
  data,
  setShowNotifyModal,
  setNotifyModalProps,
  index,
}: {
  metrics: Metrics;
  corridor: string;
  data: MetricsData[];
  setShowNotifyModal: SPCTableProps['setShowNotifyModal'];
  setNotifyModalProps: SPCTableProps['setNotifyModalProps'];
  index: number;
}) => {
  const redTextClassName = 'text-sm text-red-500';

  const processor = data.find((d) => d.corridor_pair === corridor)?.processor;
  const avgDuration = metrics.avgDurationPerCorridor[corridor];
  const prevDuration = metrics.previousAvgDurationPerCorridor[corridor];
  const deltaDuration = metrics.deltaDurationPerCorridor[corridor];
  const successRate = metrics.corridorSuccessRates[corridor];

  const rowHighlight =
    successRate < 85 ? 'bg-red-100 hover:bg-red-100' : 'hover:bg-gray-100';
  const zebraRow = index % 2 === 0 ? '' : 'bg-gray-50 dark:bg-dark-zebraRow';

  return (
    <Tr
      className={`transition-colors duration-500 ${rowHighlight} ${zebraRow}`}
    >
      <Td className={`px-3 font-medium`}>{corridor}</Td>
      <Td className={`px-2 font-medium`}>{formatTime(avgDuration)}</Td>
      <Td className={`px-2 font-medium`}>{formatTime(prevDuration)}</Td>
      <Td
        className={`px-2 font-medium ${
          deltaDuration > 0 ? redTextClassName : ''
        }`}
      >
        {formatTime(deltaDuration)}
      </Td>
      <Td className={`px-2 font-medium`}>{processor}</Td>
      <Td
        className={`px-2 font-medium ${
          successRate < 85 ? redTextClassName : ''
        }`}
      >
        {successRate.toFixed(2)}
      </Td>
      <Td className={`px-2 font-medium`}>
        <PermissionsProvider
          permission={AfriexAdminPermission.ProcessorDowntimeNotify}
        >
          <Button
            onClick={() => {
              if (!processor || !corridor) {
                return;
              }
              setNotifyModalProps({
                message: `We are currently experiencing delays on ${corridor} with a completion time of ${formatTime(avgDuration)}.`,
                selectedProcessor: processor,
                selectedCorridorPair: corridor,
                selectedPaymentInstrument: 'all',
              });
              setShowNotifyModal(true);
            }}
            variant="solid"
            colorScheme="cyan"
            className={successRate < 50 ? 'opacity-50' : ''}
            disabled={successRate < 50}
          >
            Notify
          </Button>
        </PermissionsProvider>
      </Td>
    </Tr>
  );
};

export default SuccessPerCorridorTable;
